import { AfterViewInit, Directive, DoCheck, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { PageSizePreferenceService } from '../services/page-size-preference.service';
import { Subscription } from 'rxjs';

/**
 * Remembers the selected page size for a mat-paginator across visits.
 *
 * Usage:
 * `<mat-paginator persistPageSize="all-modules.executions-list" [pageSize]="10" [pageSizeOptions]="[10, 25, 50]">`
 *
 * Hard-coded `[pageSize]="10"` bindings are re-applied on every change detection.
 * This directive keeps the stored preference applied (via DoCheck) so those bindings
 * cannot wipe the restored value after refresh.
 *
 * When a stored preference differs from the initial paginator size, it also emits a
 * deferred page event so hosts reload data with the correct page size.
 */
@Directive({
    selector: 'mat-paginator[persistPageSize]',
})
export class PersistPageSizeDirective implements AfterViewInit, OnChanges, OnDestroy, DoCheck {
    @Input('persistPageSize') tableKey: string;

    private pageSubscription: Subscription;
    private restoreTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private hasInitialized = false;
    /** Preference currently enforced against template `[pageSize]` bindings. */
    private desiredPageSize: number | null = null;
    private didEmitRestore = false;

    constructor(private readonly paginator: MatPaginator, private readonly pageSizePreferenceService: PageSizePreferenceService) {}

    ngAfterViewInit(): void {
        this.hasInitialized = true;
        this.subscribeToPageChanges();
        this.scheduleStoredPageSizeRestore();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.hasInitialized && changes.tableKey && !changes.tableKey.firstChange) {
            this.didEmitRestore = false;
            this.scheduleStoredPageSizeRestore();
        }
    }

    ngDoCheck(): void {
        if (this.desiredPageSize == null) {
            return;
        }

        if (this.paginator.pageSize !== this.desiredPageSize) {
            this.paginator.pageSize = this.desiredPageSize;
        }
    }

    ngOnDestroy(): void {
        if (this.restoreTimeoutId != null) {
            clearTimeout(this.restoreTimeoutId);
            this.restoreTimeoutId = null;
        }
        this.pageSubscription?.unsubscribe();
    }

    private scheduleStoredPageSizeRestore(): void {
        if (!this.tableKey) {
            return;
        }

        const defaultPageSize = this.paginator.pageSize || 10;
        const storedPageSize = this.pageSizePreferenceService.getPageSize(this.tableKey, this.paginator.pageSizeOptions, defaultPageSize);
        this.desiredPageSize = storedPageSize;

        // Apply immediately so the dropdown shows the correct value.
        this.paginator.pageSize = storedPageSize;

        if (storedPageSize === defaultPageSize || this.didEmitRestore) {
            return;
        }

        // Emit after host subscriptions (often set up in AfterViewInit / setTimeout from ngOnInit)
        // so the first data load uses the restored page size instead of the template default.
        if (this.restoreTimeoutId != null) {
            clearTimeout(this.restoreTimeoutId);
        }

        this.restoreTimeoutId = setTimeout(() => {
            this.restoreTimeoutId = null;
            this.didEmitRestore = true;
            this.desiredPageSize = storedPageSize;
            this.paginator.pageSize = storedPageSize;
            this.paginator._changePageSize(storedPageSize);
        });
    }

    private subscribeToPageChanges(): void {
        this.pageSubscription?.unsubscribe();
        this.pageSubscription = this.paginator.page.subscribe((event: PageEvent) => {
            if (!this.tableKey) {
                return;
            }

            this.desiredPageSize = event.pageSize;
            this.pageSizePreferenceService.setPageSize(this.tableKey, event.pageSize);
        });
    }
}
